import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { OrderingContent, OrderingStep } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Reorder tokens into the correct sequence. Tokens start shuffled; the learner
 *  moves them up/down. Graded against each step's `index`. */
export function OrderingRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as OrderingContent;
  const steps: OrderingStep[] = c.steps ?? [];
  const byId = useMemo(() => new Map(steps.map((s) => [s.token_id, s])), [steps]);

  // Initialise a shuffled order once per question (when answer is empty).
  const shuffled = useMemo(() => {
    const ids = steps.map((s) => s.token_id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Array.isArray(answer) || (answer as string[]).length !== steps.length) {
      setAnswer(shuffled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const order = (Array.isArray(answer) ? (answer as string[]) : shuffled).filter((id) => byId.has(id));
  const correctOrder = [...steps].sort((a, b) => a.index - b.index).map((s) => s.token_id);

  const move = (from: number, to: number) => {
    if (revealed || to < 0 || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setAnswer(next);
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      <div className="mt-4 space-y-2">
        {order.map((id, i) => {
          const step = byId.get(id)!;
          const rightSpot = revealed && correctOrder[i] === id;
          return (
            <div
              key={id}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm ${
                revealed
                  ? rightSpot
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-red-400 bg-red-50"
                  : "border-black/10 bg-white"
              }`}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-faint">
                {i + 1}
              </span>
              <span className="flex-1 text-ink">{step.text}</span>
              {!revealed && (
                <span className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0}
                    className="rounded-md px-2 py-1 text-ink-faint hover:bg-black/5 disabled:opacity-30"
                    aria-label={t("Move up")}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, i + 1)}
                    disabled={i === order.length - 1}
                    className="rounded-md px-2 py-1 text-ink-faint hover:bg-black/5 disabled:opacity-30"
                    aria-label={t("Move down")}
                  >
                    ↓
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
